import type {
	FailedPendingResponse,
	HTTPValidationError,
	PricingCampaignReassignRequest,
	PricingCampaignReassignResponseModel,
} from '@/shared/generated'
import type { UseMutationResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { prop } from 'lodash/fp'
import { AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PricingCampaignManagementResults } from '@/entities/pricing-campaign'

import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Spinner } from '@/shared/components/ui/spinner'

type Props = {
	formData: {
		campaigns: string[]
		groups: string[]
	}
	run: UseMutationResult<
		PricingCampaignReassignResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignReassignRequest
	>
}
const isAllSuccess = (data: PricingCampaignReassignResponseModel[]) =>
	data?.every(prop('success'))
export default function ExecutionView({ formData, run }: Props) {
	const { t } = useTranslation()

	if (run.isPending) {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Spinner className='text-muted-foreground' />
					</EmptyMedia>
					<EmptyTitle>{t('pc.processing.loading.title')}</EmptyTitle>
				</EmptyHeader>
			</Empty>
		)
	} else if (run.isError) {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon' className='bg-red-500/20'>
						<AlertTriangleIcon className='text-red-500' />
					</EmptyMedia>
					<EmptyTitle>{t('pc.processing.error.title')}</EmptyTitle>
				</EmptyHeader>
			</Empty>
		)
	} else {
		if (isAllSuccess(run.data!)) {
			return (
				<Empty className='h-full'>
					<EmptyHeader>
						<EmptyMedia variant='icon' className='bg-green-500/20'>
							<CheckCircle2Icon className='text-green-500' />
						</EmptyMedia>
						<EmptyTitle>
							{t('app.success', { defaultValue: 'Success' })}
						</EmptyTitle>
						<EmptyDescription>
							{t('pc.processing.success.title', {
								action: 'reassigned',
								campaigns: formData.campaigns.length,
								groups: 1,
							})}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			)
		} else {
			return (
				<Empty className='h-full'>
					<EmptyHeader>
						<EmptyMedia variant='icon' className='bg-orange-500/20'>
							<CheckCircle2Icon className='text-orange-500' />
						</EmptyMedia>
						<EmptyTitle>
							{t('app.success', { defaultValue: 'Success' })}
						</EmptyTitle>
						<EmptyDescription>
							{t('pc.processing.indeterminate.title', {
								action: 'reassigned',
								campaigns: run?.data?.filter(prop('success'))?.length,
								totalCampaigns: formData.campaigns.length,
								groups: 1,
								totalGroups: 1,
							})}
						</EmptyDescription>
					</EmptyHeader>
					<EmptyContent>
						<PricingCampaignManagementResults response={run?.data} />
					</EmptyContent>
				</Empty>
			)
		}
	}
}
